import type { ComponentProps } from "react";
import { getIn, type FormikProps } from "formik";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils/cn";
import { clampNumericInput } from "@/utils/number-range";

import { Field, FieldDescription, FieldError, FieldLabel } from "./ui/field";
import { AIShimmerLayer } from "./ai/ai-info-extractor/ai-shimmer-overlay";

const baseInput = "transition-all duration-300";

type ValidationState = {
  isLoading?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  errorMessage?: string;
};

type TextFieldProps = Omit<
  ComponentProps<"input">,
  "name" | "value" | "onChange"
> & {
  name: string;
  label?: string;
  /**
   * Marks the field required, rendering the asterisk consistently.
   *
   * `Select` has always had this; `Textfield` did not, so authors typed the
   * asterisk into the label string instead — `label="EARNING NAME*"`. That
   * makes required-ness a piece of copy each author has to remember, renders
   * differently from `Select`'s, and never reaches a screen reader.
   */
  required?: boolean;
  /** Optional node rendered next to the label (e.g. an InfoPopover hint). */
  labelHint?: React.ReactNode;
  formik: FormikProps<any>;
  validation?: ValidationState;
  withValidation?: boolean;
  rootClassName?: string;
  endContent?: React.ReactNode;
  startContent?: React.ReactNode;
  /**
   * Renders a `12 / 100` counter under the field. Needs `maxLength` to have
   * anything to count against; ignored without one.
   *
   * Opt-in, not automatic. On a coded field whose length IS the format — a
   * 13-digit establishment id, a 34-character IBAN — the cap is self-evident
   * and a counter under every one is noise. Turn it on for free-text names,
   * where the cap is invisible until the browser silently stops accepting
   * keystrokes and the user cannot tell the field from a broken one.
   */
  showCount?: boolean;
  /**
   * On blur, pull the value back inside `min`/`max` — the same correction
   * `NumberInput` makes in the payroll settings sections, so a number field
   * behaves the same way whichever of the two widgets it is built from.
   *
   * Opt-in. Silently rewriting what someone typed is only defensible where the
   * bound is a hard fact about the field (a percentage cannot exceed 100, a
   * month has no 99th day); where the bound is a policy limit, the error
   * message is the better answer and this stays off.
   *
   * Needs `min` or `max` to have anything to clamp to; ignored without one, or
   * on a field that is not `type="number"`.
   */
  clampOnBlur?: boolean;
  /**
   * The field's format rule, kept on screen while the field is being used.
   *
   * A placeholder disappears at the first keystroke — exactly when someone is
   * typing an IBAN or a 13-digit establishment id and needs to check the shape
   * against something. This renders under the input and stays.
   *
   * Give it the rule, not a restatement of the label. It is announced to
   * screen readers through `aria-describedby`, and an error REPLACES it: the
   * error message states the same rule, in red, and two lines saying the same
   * thing is noise.
   */
  hint?: React.ReactNode;
  /** Shimmer over the input while AI is filling it (same as `Select`). */
  showAIShimmer?: boolean;
};

export const TextField = ({
  name,
  label,
  required = false,
  labelHint,
  formik,
  className,
  validation,
  type = "text",
  rootClassName,
  endContent,
  startContent,
  showCount = false,
  clampOnBlur = false,
  hint,
  showAIShimmer = false,
  ...rest
}: TextFieldProps) => {
  // Use Formik's getIn so nested paths (e.g. "fields.0.name") resolve correctly,
  // not just flat keys.
  const fieldError = getIn(formik.errors, name);
  const fieldTouched = getIn(formik.touched, name);
  const error = (fieldTouched && fieldError) || validation?.errorMessage;
  const value = getIn(formik.values, name) ?? "";

  const { maxLength, min, max } = rest;

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (clampOnBlur && type === "number" && (min !== undefined || max !== undefined)) {
      const clamped = clampNumericInput(event.target.value, {
        min: min === undefined ? undefined : Number(min),
        max: max === undefined ? undefined : Number(max),
      });
      if (clamped !== event.target.value) formik.setFieldValue(name, clamped);
    }
    formik.handleBlur(event);
  };
  const counter =
    showCount && typeof maxLength === "number" ? (
      <span
        className={cn(
          "mt-px shrink-0 text-xs tabular-nums text-muted-foreground",
          // At the cap the browser stops accepting input with no feedback at
          // all. Colouring the counter is the only thing that says why.
          String(value).length >= maxLength && "text-warning",
        )}
      >
        {String(value).length} / {maxLength}
      </span>
    ) : null;

  const errorText =
    typeof error === "string"
      ? error
      : Array.isArray(error)
        ? error.join(", ")
        : error && typeof error === "object"
          ? JSON.stringify(error)
          : undefined;

  const hintId = `${name}-hint`;
  const errorId = `${name}-error`;

  /**
   * One line under the field, whichever it is. The error supersedes the hint
   * rather than joining it: both state the same rule, so stacking them says
   * the same thing twice and moves the fields below.
   */
  const message =
    errorText || !hint ? (
      <FieldError id={errorId}>{errorText}</FieldError>
    ) : (
      <FieldDescription id={hintId} className="text-xs">
        {hint}
      </FieldDescription>
    );

  // The caller's own `aria-describedby` is kept, not overwritten.
  const describedBy =
    [rest["aria-describedby"], errorText ? errorId : hint ? hintId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <Field className={cn("relative", rootClassName)}>
      {label && (
        <FieldLabel htmlFor={name} className="flex items-center gap-1">
          {label}
          {required && (
            <span aria-hidden="true" className="text-red-500">
              *
            </span>
          )}
          {labelHint}
        </FieldLabel>
      )}
      <div className={cn("relative w-full", showAIShimmer && "pointer-events-none")}>
        {startContent && (
          <span className="absolute inset-y-0 start-0 flex items-center ps-2">
            {startContent}
          </span>
        )}
        <Input
          {...rest}
          type={type}
          name={name}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={Boolean(errorText) || undefined}
          value={value}
          onBlur={handleBlur}
          onChange={formik.handleChange}
          className={cn(
            baseInput,
            className,
            "pe-10 text-sm placeholder:text-gray-400 appearance-none block w-full p-4 h-9 md:h-12",
            error && "border border-red-500 focus:ring-red-500/20",
            Boolean(endContent) && "pe-16",
            Boolean(startContent) && "ps-8",
          )}
        />
        {endContent && (
          <span className="absolute inset-y-0 end-0 flex items-center pe-2">
            {endContent}
          </span>
        )}
        {/* Layer inside the existing box, not an `AIShimmerOverlay` wrapper:
            an extra div would change sizing in horizontal `Field` layouts. */}
        {showAIShimmer && <AIShimmerLayer />}
      </div>
      {counter ? (
        // One row, so the counter does not add a second line of height under
        // every field it is turned on for. `FieldError` already reserves that
        // line whether or not there is an error.
        <div className="flex items-start justify-between gap-2">
          {message}
          {counter}
        </div>
      ) : (
        message
      )}
    </Field>
  );
};
